import { Capacitor } from '@capacitor/core';
import {
  CapacitorSQLite,
  SQLiteConnection,
  SQLiteDBConnection,
} from '@capacitor-community/sqlite';

const DB_NAME = 'dwf_offline';
const SCHEMA_VERSION = 1;

/**
 * Thin adapter over @capacitor-community/sqlite for offline data persistence.
 *
 * All calls are guarded behind `Capacitor.isNativePlatform()` — on web this
 * module is a no-op (offline features are native-only).
 *
 * Tables:
 *   meta      — key/value store (TanStack Query cache, schema version)
 *   entities  — prefetched entity data for offline reads
 *   drafts    — form draft autosave
 *   mutations — queued offline mutations
 *   media     — media upload metadata (blobs stored on filesystem)
 *   conflicts — local conflict tracking
 *   logs      — diagnostic ring buffer overflow
 */

let sqlite: SQLiteConnection | null = null;
let db: SQLiteDBConnection | null = null;
let openPromise: Promise<void> | null = null;

function isNative(): boolean {
  return Capacitor.isNativePlatform();
}

const CREATE_TABLES = `
  CREATE TABLE IF NOT EXISTS meta (
    key   TEXT PRIMARY KEY,
    value TEXT
  );

  CREATE TABLE IF NOT EXISTS entities (
    cache_key  TEXT NOT NULL,
    entity_id  TEXT NOT NULL,
    data       TEXT NOT NULL,
    fetched_at INTEGER NOT NULL,
    PRIMARY KEY (cache_key, entity_id)
  );

  CREATE TABLE IF NOT EXISTS drafts (
    template_id   TEXT NOT NULL,
    work_order_id TEXT NOT NULL,
    user_id       TEXT NOT NULL,
    response      TEXT NOT NULL,
    last_edited_at INTEGER NOT NULL,
    PRIMARY KEY (template_id, work_order_id, user_id)
  );

  CREATE TABLE IF NOT EXISTS mutations (
    id          TEXT PRIMARY KEY,
    data        TEXT NOT NULL,
    created_at  INTEGER NOT NULL
  );

  CREATE TABLE IF NOT EXISTS media (
    id           TEXT PRIMARY KEY,
    entity_type  TEXT NOT NULL,
    entity_id    TEXT NOT NULL,
    name         TEXT NOT NULL,
    mime_type    TEXT NOT NULL,
    size         INTEGER NOT NULL,
    file_path    TEXT NOT NULL,
    status       TEXT NOT NULL DEFAULT 'pending',
    attempt_count INTEGER NOT NULL DEFAULT 0,
    uploaded_url TEXT,
    error        TEXT,
    created_at   INTEGER NOT NULL
  );

  CREATE TABLE IF NOT EXISTS conflicts (
    id          TEXT PRIMARY KEY,
    data        TEXT NOT NULL,
    created_at  INTEGER NOT NULL
  );

  CREATE TABLE IF NOT EXISTS logs (
    id         INTEGER PRIMARY KEY AUTOINCREMENT,
    level      TEXT NOT NULL,
    message    TEXT NOT NULL,
    timestamp  INTEGER NOT NULL
  );
`;

async function initDb(): Promise<void> {
  if (!isNative()) return;

  sqlite = new SQLiteConnection(CapacitorSQLite);

  const consistencyCheck = (await sqlite.checkConnectionsConsistency()).result;
  const isConnected = (await sqlite.isConnection(DB_NAME, false)).result;

  if (consistencyCheck && isConnected) {
    db = await sqlite.retrieveConnection(DB_NAME, false);
  } else {
    db = await sqlite.createConnection(DB_NAME, false, 'no-encryption', SCHEMA_VERSION, false);
  }

  await db.open();

  // Create tables first (idempotent — IF NOT EXISTS)
  await db.execute(CREATE_TABLES);

  // Check schema version — throwaway on major bump
  const versionResult = await db.query(
    `SELECT value FROM meta WHERE key = 'schema_version'`,
  );
  const storedVersion = versionResult.values?.[0]?.value;

  if (storedVersion && Number(storedVersion) !== SCHEMA_VERSION) {
    // Wipe and recreate (v1 throwaway strategy)
    const tables = ['meta', 'entities', 'drafts', 'mutations', 'media', 'conflicts', 'logs'];
    for (const table of tables) {
      await db.execute(`DROP TABLE IF EXISTS ${table}`);
    }
    await db.execute(CREATE_TABLES);
  }

  // Upsert schema version
  await db.run(
    `INSERT OR REPLACE INTO meta (key, value) VALUES ('schema_version', ?)`,
    [String(SCHEMA_VERSION)],
  );
}

/**
 * Open the SQLite database. Safe to call multiple times — only initialises once.
 * No-op on web (non-native platforms).
 */
export async function open(): Promise<void> {
  if (!isNative()) return;
  if (db) return;
  if (!openPromise) {
    openPromise = initDb();
  }
  return openPromise;
}

/** Get the active database connection. Throws if not open or not native. */
function getDb(): SQLiteDBConnection {
  if (!db) throw new Error('SQLite not initialised — call open() first');
  return db;
}

// ---------------------------------------------------------------------------
// Key-value helpers for the `meta` table (drop-in for idb-keyval get/set/del)
// ---------------------------------------------------------------------------

export async function get<T = string>(key: string): Promise<T | undefined> {
  if (!isNative()) return undefined;
  await open();
  const result = await getDb().query(
    `SELECT value FROM meta WHERE key = ?`,
    [key],
  );
  const raw = result.values?.[0]?.value;
  if (raw === undefined || raw === null) return undefined;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return raw as unknown as T;
  }
}

export async function set(key: string, value: unknown): Promise<void> {
  if (!isNative()) return;
  await open();
  const serialised = typeof value === 'string' ? value : JSON.stringify(value);
  await getDb().run(
    `INSERT OR REPLACE INTO meta (key, value) VALUES (?, ?)`,
    [key, serialised],
  );
}

export async function del(key: string): Promise<void> {
  if (!isNative()) return;
  await open();
  await getDb().run(`DELETE FROM meta WHERE key = ?`, [key]);
}

/**
 * Get a raw string value from the meta table WITHOUT JSON.parse.
 * Used by the TanStack Query persister which handles its own serialization.
 */
export async function getRaw(key: string): Promise<string | null> {
  if (!isNative()) return null;
  await open();
  const result = await getDb().query(
    `SELECT value FROM meta WHERE key = ?`,
    [key],
  );
  return result.values?.[0]?.value ?? null;
}

/**
 * Set a raw string value in the meta table WITHOUT JSON.stringify.
 * Used by the TanStack Query persister which handles its own serialization.
 */
export async function setRaw(key: string, value: string): Promise<void> {
  if (!isNative()) return;
  await open();
  await getDb().run(
    `INSERT OR REPLACE INTO meta (key, value) VALUES (?, ?)`,
    [key, value],
  );
}

// ---------------------------------------------------------------------------
// Generic SQL helpers
// ---------------------------------------------------------------------------

export async function query<T = Record<string, unknown>>(
  sql: string,
  params: unknown[] = [],
): Promise<T[]> {
  if (!isNative()) return [];
  await open();
  const result = await getDb().query(sql, params);
  return (result.values ?? []) as T[];
}

export async function run(
  sql: string,
  params: unknown[] = [],
): Promise<{ changes: number; lastId: number }> {
  if (!isNative()) return { changes: 0, lastId: 0 };
  await open();
  const result = await getDb().run(sql, params);
  return {
    changes: result.changes?.changes ?? 0,
    lastId: result.changes?.lastId ?? 0,
  };
}

export async function exec(sql: string): Promise<void> {
  if (!isNative()) return;
  await open();
  await getDb().execute(sql);
}

// ---------------------------------------------------------------------------
// Cleanup
// ---------------------------------------------------------------------------

/** Wipe all data from all tables (used on logout). */
export async function clearAll(): Promise<void> {
  if (!isNative() || !db) return;
  const tables = ['meta', 'entities', 'drafts', 'mutations', 'media', 'conflicts', 'logs'];
  for (const table of tables) {
    await db.run(`DELETE FROM ${table}`);
  }
  // Re-insert schema version after wipe
  await db.run(
    `INSERT OR REPLACE INTO meta (key, value) VALUES ('schema_version', ?)`,
    [String(SCHEMA_VERSION)],
  );
}

/** Check whether we're running on a native platform with SQLite available. */
export { isNative };
